import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, OTPStep } from '@unified-checkout/design-system';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { userActivationVerifyOtp, userActivationResendOtp } from '@app/dashboard/services/outer_layout/user-activation';
import type { IUserActivationOtpStep } from '@app/dashboard/types/outerLayout/user-activation';


const SUBMIT_BTN_ID = 'user-activation-form-otp-submit-btn';

const OtpStep = (
  {
    loading,
    setLoading,
    setCurrentStep,
    otpToken,
    STEPS,
    fields,
    FieldNames,
    isInvitedBy,
    redirectToLogin,
    LOGIN_REDIRECT_TIMEOUT
  }: IUserActivationOtpStep
) => {
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const [otp, setOtp] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');
  const [serverSuccessMsg, setServerSuccessMsg] = useState('');
  const [otpResetTimerKey, setOtpResetTimerKey] = useState(0);

  useEffect(() => {
    setServerErrMsg("");
    setServerSuccessMsg("");
  }, [otp]);

  const onValidHandler = (valid: boolean) => {
    setIsValid(valid);
  };

  const onResendOTP = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    const phone = fields[FieldNames.PHONE_NUMBER].value;

    const args = {
      token: otpToken as string,
      userNameType: isInvitedBy,
      email: fields[FieldNames.EMAIL].value as string,
      ...((phone) ? { phone } : {}),
    };

    userActivationResendOtp(args)
      .then(() => {
        setOtpResetTimerKey(prevKey => prevKey + 1);
        setIsValid(false);
        setServerSuccessMsg(t('form.otpResent'));
      })
      .catch((error) => {
        const TOO_MANY_REQS_CODE = 429;
        const status = error.response?.status;

        setServerErrMsg(
          status === TOO_MANY_REQS_CODE ?
            t('outerLayout.form.errors.tooManyAttempts')
            :
            t('errors.somethingWentWrong')
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOtpSubmit = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    const phone = fields[FieldNames.PHONE_NUMBER].value;

    const args = {
      token: otpToken as string,
      userNameType: isInvitedBy,
      fullName: fields[FieldNames.FULL_NAME].value as string,
      email: fields[FieldNames.EMAIL].value as string,
      ...((phone) ? { phone } : {}),
      password: fields[FieldNames.PASSWORD].value as string,
      otp
    };

    userActivationVerifyOtp(args)
      .then(() => {
        setCurrentStep(STEPS.SUCCESSFUL_ACTIVATION);

        setTimeout(() => {
          redirectToLogin();
        }, LOGIN_REDIRECT_TIMEOUT);
      })
      .catch((error) => {
        const data = error.response?.data;
        const message = data?.message;
        const status = data?.status;

        status === false && setServerErrMsg(message || t('errors.somethingWentWrong'))
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <OTPStep
        otp={otp}
        setOtp={setOtp}
        otpResetTimerKey={otpResetTimerKey}
        onValid={onValidHandler}
        onResendOTP={onResendOTP}
        resendText={t('form.resendCodeIn')}
        resendBtnText={t('form.resend')}
        resendTimer={150}
        length={6}
        loading={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && !serverSuccessMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        {serverSuccessMsg && !serverErrMsg && <div className='form__submit__server-success'>{serverSuccessMsg}</div>}

        <div className='form__submit__action-btns'>
          <Button
            size='md'
            color='primary'
            variant='outlined'
            text={t('innerLayout.userManagementPage.form.skip&VerifyLater')}
            onClickHandler={() => redirectToLogin()}
          />

          <Button
            id={SUBMIT_BTN_ID}
            size='md'
            color='primary'
            variant='contained'
            text={loading ? undefined : t('innerLayout.userManagementPage.form.verifyNow')}
            loading={loading}
            disabled={!isValid || loading}
            onClickHandler={handleOtpSubmit}
          />
        </div>
      </div>
    </>
  )
}

export default OtpStep;